import { useState, useEffect, useRef, useCallback } from "react";
import { listBooks, subscribeBooks, getBook, setCover, claimMilestone } from "../api/books";
import fetchCover from "../utils/fetchCover";

export default function useBookData(currentUser, setScreen) {
  const [myBooks, setMyBooks] = useState([]);
  const [allBooksData, setAllBooksData] = useState([]);
  const [coversLoaded, setCoversLoaded] = useState(false);
  const [appLoading, setAppLoading] = useState(false);
  /** "MUROO의 111번째 책" 을 잡은 순간. null 이면 평소다. */
  const [milestone, setMilestone] = useState(null);
  const appLoadingRef = useRef(false);

  const startLoading = () => {
    setAppLoading(true);
    appLoadingRef.current = true;
  };

  const uid = currentUser?.uid;
  const isGuest = currentUser?.isAnonymous;

  /** 내 서재 = user_books 에 내 행이 있는 책. 예전 users.bookIds 필터를 대체한다. */
  const pickMine = useCallback(
    (books) => (isGuest ? books : books.filter((b) => b.registeredBy?.[uid] !== undefined)),
    [uid, isGuest]
  );

  useEffect(() => {
    if (!currentUser) return;
    let alive = true;

    const load = async () => {
      try {
        const allBooks = await listBooks();
        if (!alive) return;
        setAllBooksData(allBooks);

        const mine = pickMine(allBooks);

        // 표지가 없는 책은 외부에서 받아와 채워 넣는다 (기존 동작 유지)
        const filled = await Promise.all(
          mine.map(async (book) => {
            if (book.cover) return book;
            const cover = await fetchCover(book.title, book.author);
            if (cover) {
              try {
                await setCover(book.id, cover);
              } catch {
                // 표지 저장 실패는 화면 표시를 막지 않는다
              }
            }
            return { ...book, cover };
          })
        );
        if (!alive) return;

        setMyBooks(filled);
        setCoversLoaded(true);
        if (appLoadingRef.current) {
          setScreen("home");
          setAppLoading(false);
          appLoadingRef.current = false;
        }
      } catch (e) {
        console.error("책 로드 실패:", e.message);
        if (alive && appLoadingRef.current) {
          setAppLoading(false);
          appLoadingRef.current = false;
        }
      }
    };

    load();
    const unsub = subscribeBooks(load);
    return () => {
      alive = false;
      unsub();
    };
    // 객체 자체가 아니라 "누구인지"에만 반응한다.
    // currentUser 는 인증 이벤트마다 새 객체로 만들어지므로,
    // 객체를 의존성에 두면 같은 사람인데도 로딩이 계속 재시작된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isGuest]);

  const handleAddBook = async (newBook, isNewToMuroo = false) => {
    const merge = (prev) =>
      prev.some((b) => b.id === newBook.id)
        ? prev.map((b) => (b.id === newBook.id ? { ...b, ...newBook } : b))
        : [newBook, ...prev];
    setMyBooks(merge);
    setAllBooksData(merge);

    // 111번째 책이면 축하 화면을 띄운다. 실패해도 등록은 이미 끝났으므로
    // 조용히 넘어간다 — 축하가 안 뜬다고 책이 안 담기면 안 된다.
    try {
      const hit = await claimMilestone({ ...newBook, isNewToMuroo }, uid);
      if (hit) setMilestone(hit);
    } catch (e) {
      console.error("기념 확인 실패:", e.message);
    }
  };

  const reloadBooks = async () => {
    try {
      const allBooks = await listBooks();
      setAllBooksData(allBooks);
      // 이미 받아둔 표지는 유지한다 (재조회로 깜빡이지 않게)
      setMyBooks((prev) =>
        pickMine(allBooks).map((book) => ({
          ...book,
          cover: book.cover || prev.find((b) => b.id === book.id)?.cover || null,
        }))
      );
    } catch (e) {
      console.error("책 갱신 실패:", e.message);
    }
  };

  const handleBookCompleted = (bookId, userId, dateStr) => {
    setMyBooks((prev) =>
      prev.map((b) =>
        b.id === bookId ? { ...b, completedBy: { ...b.completedBy, [userId]: dateStr } } : b
      )
    );
  };

  const handleBookRemoved = (bookId) => {
    setMyBooks((prev) => prev.filter((b) => b.id !== bookId));
  };

  const handleMemoChanged = async (bookId) => {
    try {
      const updated = await getBook(bookId);
      if (!updated) return;
      const patch = (prev) => prev.map((b) => (b.id === bookId ? { ...b, ...updated } : b));
      setMyBooks(patch);
      setAllBooksData(patch);
    } catch {
      // 메모 반영 실패는 다음 실시간 갱신에서 따라잡는다
    }
  };

  return {
    myBooks,
    setMyBooks,
    allBooksData,
    setAllBooksData,
    coversLoaded,
    appLoading,
    startLoading,
    handleAddBook,
    milestone,
    clearMilestone: () => setMilestone(null),
    reloadBooks,
    handleBookCompleted,
    handleBookRemoved,
    handleMemoChanged,
  };
}
